import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { CampaignsTab } from "@/pages/AdminCampaigns";
import ShiftsTab from "@/pages/AdminShifts";
import TeamMeetings from "@/pages/TeamMeetings";
import AdminExclusionLists from "@/pages/AdminExclusionLists";
import AdminEmailCampaigns from "@/pages/AdminEmailCampaigns";
import AdminProductivity from "@/pages/AdminProductivity";
import AdminReports from "@/pages/AdminReports";
import AgentsTab from "@/pages/AdminAgents";
import AdminShiftSchedules from "@/pages/AdminShiftSchedules";
import AdminSystemHealth from "@/pages/AdminSystemHealth";
import AdminSupport from "@/pages/AdminSupport";
import AdminSbtStats from "@/pages/AdminSbtStats";
import AdminSbtAudienceCounts from "@/pages/AdminSbtAudienceCounts";
import AdminSbtTrendingTopics from "@/pages/AdminSbtTrendingTopics";
import AdminSbtLandingPages from "@/pages/AdminSbtLandingPages";
import AdminSbtSponsors from "@/pages/AdminSbtSponsors";
import AdminSbtSite from "@/pages/AdminSbtSite";
import AdminGrowthEngine from "@/pages/AdminGrowthEngine";
import AdminRuleBook from "@/pages/AdminRuleBook";
import RolePermissionsDialog from "@/pages/RolePermissionsDialog";
import { Shield, Users } from "lucide-react";

function ResourceTable({ kind, items, onCreate, onUpdate, onDelete }) {
  const [form, setForm] = useState({ name: "", email: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", email: "", description: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      await onCreate(form);
      setForm({ name: "", email: "", description: "" });
    } finally { setLoading(false); }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditDraft({ name: it.name || "", email: it.email || "", description: it.description || "" });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    if (!editDraft.name.trim()) return toast.error("Name is required");
    await onUpdate(id, editDraft);
    setEditingId(null);
  };

  const secondaryKey = kind === "campaigns" ? "description" : "email";
  const secondaryLabel = kind === "campaigns" ? "Description" : "Email";

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="flex flex-wrap gap-2 items-end" data-testid={`${kind}-create-form`}>
        <div className="flex-1 min-w-[200px]">
          <label className="meta-label">Name</label>
          <Input
            data-testid={`${kind}-name-input`}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={kind === "campaigns" ? "e.g. EMEA Spring Wave" : "e.g. Aisha Khan"}
            className="rounded-sm h-10 mt-1"
            required
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="meta-label">{secondaryLabel}</label>
          <Input
            data-testid={`${kind}-${secondaryKey}-input`}
            value={form[secondaryKey]}
            onChange={(e) => setForm({ ...form, [secondaryKey]: e.target.value })}
            placeholder="Optional"
            className={`rounded-sm h-10 mt-1 ${secondaryKey === "email" ? "font-mono" : ""}`}
          />
        </div>
        <Button type="submit" data-testid={`${kind}-create-button`} disabled={loading} className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </form>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left border-b border-border">
              <th className="px-4 py-2 meta-label">Name</th>
              <th className="px-4 py-2 meta-label">{secondaryLabel}</th>
              <th className="px-4 py-2 meta-label">Created</th>
              <th className="px-4 py-2 meta-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Nothing yet.</td></tr>
            )}
            {items.map((it) => {
              const isEdit = editingId === it.id;
              return (
                <tr key={it.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-medium align-middle">
                    {isEdit ? (
                      <Input
                        autoFocus
                        data-testid={`${kind}-edit-name-${it.id}`}
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        className="rounded-sm h-9"
                      />
                    ) : it.name}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs align-middle">
                    {isEdit ? (
                      <Input
                        data-testid={`${kind}-edit-${secondaryKey}-${it.id}`}
                        value={editDraft[secondaryKey]}
                        onChange={(e) => setEditDraft({ ...editDraft, [secondaryKey]: e.target.value })}
                        className="rounded-sm h-9"
                      />
                    ) : (it[secondaryKey] || "—")}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground align-middle">{(it.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right align-middle">
                    {isEdit ? (
                      <div className="inline-flex items-center gap-2">
                        <button
                          data-testid={`${kind}-save-${it.id}`}
                          onClick={() => saveEdit(it.id)}
                          className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <Check size={12} /> Save
                        </button>
                        <button
                          data-testid={`${kind}-cancel-${it.id}`}
                          onClick={cancelEdit}
                          className="text-muted-foreground hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <X size={12} /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-3">
                        <button
                          data-testid={`${kind}-edit-${it.id}`}
                          onClick={() => startEdit(it)}
                          className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          data-testid={`delete-${kind}-${it.id}`}
                          onClick={() => onDelete(it.id)}
                          className="text-red-600 hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ManageRolesPanel({ roles, onCreate, onUpdate, onDelete, onEditPermissions }) {
  const [draft, setDraft] = useState({ name: "", base_role: "agent" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", base_role: "agent" });

  const submit = async (e) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name) return toast.error("Role name is required");
    setBusy(true);
    try {
      await onCreate({ name, base_role: draft.base_role });
      setDraft({ name: "", base_role: "agent" });
    } finally { setBusy(false); }
  };

  const startEdit = (r) => { setEditingId(r.id); setEditDraft({ name: r.name, base_role: r.base_role }); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id) => {
    if (!editDraft.name.trim()) return toast.error("Role name cannot be empty");
    await onUpdate(id, editDraft);
    setEditingId(null);
  };

  return (
    <div className="border border-border p-4 rounded-sm bg-secondary/20 space-y-3" data-testid="manage-roles-panel">
      <div className="flex items-baseline gap-2">
        <div className="meta-label">Manage Roles</div>
        <p className="text-xs text-muted-foreground">
          Add custom role labels (e.g. <span className="font-mono">HR Team Lead</span>, <span className="font-mono">Team Manager</span>). Each maps to a built-in permission base (Admin / Agent / QA).
        </p>
      </div>
      <form onSubmit={submit} className="flex flex-wrap gap-2 items-end" data-testid="role-create-form">
        <div className="flex-1 min-w-[220px]">
          <label className="meta-label">New Role Name</label>
          <Input
            data-testid="role-name-input"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. HR Team Lead"
            className="rounded-sm h-9 mt-1"
          />
        </div>
        <div>
          <label className="meta-label">Permission base</label>
          <select
            data-testid="role-base-select"
            value={draft.base_role}
            onChange={(e) => setDraft({ ...draft, base_role: e.target.value })}
            className="mt-1 px-3 h-9 border border-input bg-background text-sm rounded-sm"
          >
            <option value="admin">Admin (full access)</option>
            <option value="qa">QA (audit-only fields)</option>
            <option value="agent">Agent (own assigned leads)</option>
          </select>
        </div>
        <Button type="submit" data-testid="role-create-button" disabled={busy} className="rounded-sm bg-primary hover:bg-[#00227A] h-9">
          <Plus size={14} className="mr-1" /> Add Role
        </Button>
      </form>
      {roles.length > 0 && (
        <div className="border border-border bg-background">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left border-b border-border">
                <th className="px-3 py-1.5 meta-label">Role Name</th>
                <th className="px-3 py-1.5 meta-label">Permission base</th>
                <th className="px-3 py-1.5 meta-label">Created</th>
                <th className="px-3 py-1.5 meta-label text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => {
                const isEdit = editingId === r.id;
                return (
                  <tr key={r.id} className="border-b border-border last:border-0" data-testid={`role-row-${r.id}`}>
                    <td className="px-3 py-1.5 font-medium">
                      {isEdit ? (
                        <Input autoFocus data-testid={`role-edit-name-${r.id}`} value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="rounded-sm h-8" />
                      ) : r.name}
                    </td>
                    <td className="px-3 py-1.5">
                      {isEdit ? (
                        <select data-testid={`role-edit-base-${r.id}`} value={editDraft.base_role} onChange={(e) => setEditDraft({ ...editDraft, base_role: e.target.value })} className="px-2 h-8 border border-input bg-background text-sm rounded-sm">
                          <option value="admin">Admin</option>
                          <option value="qa">QA</option>
                          <option value="agent">Agent</option>
                        </select>
                      ) : (
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm ${r.base_role === "admin" ? "badge-info" : r.base_role === "qa" ? "badge-warning" : "badge-neutral"}`}>
                          {r.base_role.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-1.5 text-right">
                      {isEdit ? (
                        <div className="inline-flex items-center gap-2">
                          <button data-testid={`role-save-${r.id}`} onClick={() => saveEdit(r.id)} className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1">
                            <Check size={12} /> Save
                          </button>
                          <button data-testid={`role-cancel-${r.id}`} onClick={cancelEdit} className="text-muted-foreground hover:underline text-xs inline-flex items-center gap-1">
                            <X size={12} /> Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-3">
                          <button data-testid={`role-permissions-${r.id}`} onClick={() => onEditPermissions(r)} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                            <Shield size={12} /> Permissions
                          </button>
                          <button
                            data-testid={`role-apply-to-base-${r.id}`}
                            onClick={async () => {
                              const baseLabel = r.base_role || "agent";
                              if (!window.confirm(
                                `Assign role "${r.name}" to ALL active users whose base role is "${baseLabel}"?\n\nThis will overwrite their current role label.`,
                              )) return;
                              try {
                                const { data } = await api.post(`/roles/${r.id}/assign-to-base`);
                                toast.success(`Applied "${data.role_name}" to ${data.updated_count} user${data.updated_count === 1 ? "" : "s"}.`);
                              } catch (e) {
                                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to apply role");
                              }
                            }}
                            className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1"
                            title={`One-click: stamp this role onto every active ${r.base_role || "agent"}`}
                          >
                            <Users size={12} /> Apply to all
                          </button>
                          <button data-testid={`role-edit-${r.id}`} onClick={() => startEdit(r)} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                            <Pencil size={12} /> Edit
                          </button>
                          <button data-testid={`role-delete-${r.id}`} onClick={() => onDelete(r.id, r.name)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsersTab({ currentUser, onRolesChanged, onEditPermissions }) {
  const [users, setUsers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [roles, setRoles] = useState([]); // custom roles
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "agent" });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", role: "agent", password: "" });

  const allRoleOptions = useMemo(() => {
    const builtins = [
      { value: "agent", label: "Agent (built-in)" },
      { value: "qa", label: "QA (built-in)" },
      { value: "admin", label: "Admin (built-in)" },
    ];
    const custom = roles.map((r) => ({ value: r.name, label: `${r.name} (custom · ${r.base_role})` }));
    return [...builtins, ...custom];
  }, [roles]);

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(u.data);
      setRoles(r.data);
      onRolesChanged?.(); // let parent (Admin) refresh its copy of roles for the Agents tab
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ email: "", password: "", name: "", role: "agent" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditDraft({ name: u.name || "", role: u.role_label || u.role, password: "" });
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id) => {
    try {
      const payload = { name: editDraft.name, role: editDraft.role };
      if (editDraft.password) payload.password = editDraft.password;
      await api.put(`/users/${id}`, payload);
      toast.success("User updated");
      setEditingId(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reactivate = async (id) => {
    try {
      await api.put(`/users/${id}`, { active: true });
      toast.success("User reactivated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createRole = async (payload) => {
    try {
      await api.post("/roles", payload);
      toast.success(`Role '${payload.name}' added`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updateRole = async (id, payload) => {
    try {
      await api.put(`/roles/${id}`, payload);
      toast.success("Role updated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteRole = async (id, name) => {
    if (!window.confirm(`Delete role '${name}'? Users assigned to it must be moved first.`)) return;
    try {
      await api.delete(`/roles/${id}`);
      toast.success("Role deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <ManageRolesPanel roles={roles} onCreate={createRole} onUpdate={updateRole} onDelete={deleteRole} onEditPermissions={onEditPermissions} />

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end border border-border p-4 rounded-sm" data-testid="users-create-form">
        <div>
          <label className="meta-label">Email</label>
          <Input data-testid="users-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-sm h-10 mt-1 font-mono" />
        </div>
        <div>
          <label className="meta-label">Name</label>
          <Input data-testid="users-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm h-10 mt-1" />
        </div>
        <div>
          <label className="meta-label">Role</label>
          <select
            data-testid="users-role-select"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full mt-1 px-3 h-10 border border-input bg-background text-sm rounded-sm"
          >
            {allRoleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="meta-label">Password</label>
          <Input data-testid="users-password-input" type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="rounded-sm h-10 mt-1 font-mono" />
        </div>
        <div className="md:col-span-4">
          <Button type="submit" data-testid="users-create-button" disabled={loading} className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
            <Plus size={14} className="mr-1" /> Create User
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-end">
        {/* Deleted users are SOFT-deleted (kept for lead/shift attribution).
            They are hidden here by default so "Delete" visibly removes them. */}
        <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-testid="users-show-inactive-toggle"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded-sm"
          />
          Show deleted / inactive ({users.filter((x) => x.active === false).length})
        </label>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left border-b border-border">
              <th className="px-4 py-2 meta-label">Name</th>
              <th className="px-4 py-2 meta-label">Email</th>
              <th className="px-4 py-2 meta-label">Role</th>
              <th className="px-4 py-2 meta-label">Campaign Access</th>
              <th className="px-4 py-2 meta-label">Bulk Upload</th>
              <th className="px-4 py-2 meta-label">Trusted Upload</th>
              <th className="px-4 py-2 meta-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No users yet.</td></tr>
            )}
            {users.filter((u) => showInactive || u.active !== false).map((u) => {
              const isEdit = editingId === u.id;
              const isSelf = u.id === currentUser?.id;
              const isInactive = u.active === false;
              const displayRole = u.role_label || u.role;
              const baseRole = u.role; // resolved base from backend list response? No - list returns stored role only.
              // For built-in roles, display = stored. For custom, display = name; base = looked up via roles list.
              const customMeta = roles.find((r) => r.name === displayRole);
              const tone = customMeta
                ? (customMeta.base_role === "admin" ? "badge-info" : customMeta.base_role === "qa" ? "badge-warning" : "badge-neutral")
                : (displayRole === "admin" ? "badge-info" : displayRole === "qa" ? "badge-warning" : "badge-neutral");
              return (
                <tr key={u.id} className={`border-b border-border last:border-0 ${isInactive ? "opacity-55 bg-secondary/20" : ""}`}>
                  <td className="px-4 py-2 align-middle">
                    {isEdit ? (
                      <Input data-testid={`users-edit-name-${u.id}`} value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="rounded-sm h-9" />
                    ) : (<>{u.name}{isInactive && <span className="ml-2 badge-warning px-1.5 py-0.5 rounded-sm text-[10px]" data-testid={`users-inactive-badge-${u.id}`}>INACTIVE</span>}</>)}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs align-middle">{u.email}{isSelf && <span className="ml-2 badge-info px-1.5 py-0.5 rounded-sm text-[10px]">you</span>}</td>
                  <td className="px-4 py-2 align-middle">
                    {isEdit ? (
                      <select
                        data-testid={`users-edit-role-${u.id}`}
                        value={editDraft.role}
                        onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })}
                        className="px-2 h-9 border border-input bg-background text-sm rounded-sm"
                      >
                        {allRoleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm ${tone}`}>
                        {displayRole.toUpperCase()}{customMeta ? <span className="opacity-70 ml-1">· {customMeta.base_role}</span> : null}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {/* Per-user override for campaign-access. Inline select — saves immediately
                        on change so admins don't need a separate edit dance for it. */}
                    <select
                      data-testid={`users-campaign-access-${u.id}`}
                      value={u.campaign_access_override || ""}
                      onChange={async (e) => {
                        const next = e.target.value || null;
                        try {
                          await api.put(`/users/${u.id}/campaign-access`, { override: next });
                          toast.success(`Campaign access updated for ${u.email}`);
                          await load();
                        } catch (err) {
                          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
                        }
                      }}
                      className="px-2 h-8 border border-input bg-background text-xs rounded-sm"
                    >
                      <option value="">Role default</option>
                      <option value="all">All campaigns</option>
                      <option value="assigned">Assigned only</option>
                    </select>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {/* Per-user grant for Bulk Upload. Additive — does not
                        downgrade users whose role already includes the
                        bulk_upload page permission. */}
                    <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        data-testid={`users-bulk-upload-grant-${u.id}`}
                        checked={u.bulk_upload_grant === true}
                        onChange={async (e) => {
                          const granted = e.target.checked;
                          try {
                            await api.put(`/users/${u.id}/bulk-upload-grant`, { granted });
                            toast.success(`Bulk upload ${granted ? "granted to" : "revoked from"} ${u.email}`);
                            await load();
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
                          }
                        }}
                        className="rounded-sm"
                      />
                      <span className="text-muted-foreground">{u.bulk_upload_grant === true ? "Granted" : "—"}</span>
                    </label>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {/* Per-user grant for TRUSTED Bulk Upload (skip ZeroBounce).
                        Independent from bulk_upload_grant — admin must enable
                        BOTH if a non-admin user should be able to do trusted
                        uploads. */}
                    <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        data-testid={`users-trusted-bulk-upload-grant-${u.id}`}
                        checked={u.trusted_bulk_upload_grant === true}
                        onChange={async (e) => {
                          const granted = e.target.checked;
                          try {
                            await api.put(`/users/${u.id}/trusted-bulk-upload-grant`, { granted });
                            toast.success(`Trusted upload ${granted ? "granted to" : "revoked from"} ${u.email}`);
                            await load();
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
                          }
                        }}
                        className="rounded-sm"
                      />
                      <span className="text-muted-foreground">{u.trusted_bulk_upload_grant === true ? "Trusted" : "—"}</span>
                    </label>
                  </td>
                  <td className="px-4 py-2 text-right align-middle">
                    {isEdit ? (
                      <div className="inline-flex items-center gap-2">
                        <Input data-testid={`users-edit-password-${u.id}`} type="password" placeholder="New password (optional)" minLength={6} value={editDraft.password} onChange={(e) => setEditDraft({ ...editDraft, password: e.target.value })} className="rounded-sm h-8 w-44 font-mono inline-block" />
                        <button data-testid={`users-save-${u.id}`} onClick={() => saveEdit(u.id)} className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1">
                          <Check size={12} /> Save
                        </button>
                        <button data-testid={`users-cancel-${u.id}`} onClick={cancelEdit} className="text-muted-foreground hover:underline text-xs inline-flex items-center gap-1">
                          <X size={12} /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-3">
                        <button data-testid={`users-edit-${u.id}`} onClick={() => startEdit(u)} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                          <Pencil size={12} /> Edit
                        </button>
                        {!isSelf && (isInactive ? (
                          <button data-testid={`reactivate-users-${u.id}`} onClick={() => reactivate(u.id)} className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1">
                            <Check size={12} /> Reactivate
                          </button>
                        ) : (
                          <button data-testid={`delete-users-${u.id}`} onClick={() => remove(u.id)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                            <Trash2 size={12} /> Delete
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, refreshOptions, options } = useAuth();
  const [agents, setAgents] = useState([]);
  const [adminRoles, setAdminRoles] = useState([]);
  const [campaignsCount, setCampaignsCount] = useState(0);
  const [permsRole, setPermsRole] = useState(null);
  const [pagePerms, setPagePerms] = useState(null);

  // Fetch which Admin sub-pages the user is allowed to see. Users with full
  // `admin` access see every tab; partial-access users (e.g., Team Lead with
  // only `admin_campaigns` + `admin_shifts`) see just those tabs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        if (!cancelled) setPagePerms(data.page_permissions || {});
      } catch {
        if (!cancelled) setPagePerms({});
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  const canSee = (key) => {
    if (!pagePerms) return true; // optimistic while loading
    return pagePerms.admin === true || pagePerms[key] === true;
  };
  const showCampaigns = canSee("admin_campaigns");
  const showShifts = canSee("admin_shifts");
  const showAgents = canSee("admin_agents");
  const showUsers = canSee("admin_users");
  const showExclusions = canSee("admin_exclusion_lists");
  const showEmailCampaigns = canSee("admin_email_campaigns");
  const showProductivity = canSee("admin_productivity") || canSee("admin_productivity_view");
  // True only when the user has full edit access. View-only users see the
  // tab + table but the edit/create/delete affordances are hidden.
  const canEditProductivity = canSee("admin_productivity");
  const showReports = canSee("admin_reports");
  const showShiftSchedules = canSee("admin_shift_schedules");
  // System Health (self-heal) — admin-only. Uses the same admin_users perm
  // since it exposes user-level data and lets admin apply fixes.
  const showSystemHealth = canSee("admin_users");
  // Support Agent escalation queue — admin or roles granted admin_support.
  const showSupport = canSee("admin_support");
  // Team Meetings tab — admin OR any role with `manage_team_meetings` flag.
  // Placed right after Reports per admin spec so it lives inside the
  // "Shift → Reports → Team Meetings" grouping.
  const showTeamMeetings = canSee("admin") || pagePerms?.manage_team_meetings === true;
  // SBT marketing website management — gate behind admin-only "admin_campaigns" perm
  // (same one used for campaigns); avoids creating a brand-new permission row.
  const showSbtSite = canSee("admin_campaigns");
  // Rule Book — base admin ONLY. Uses the raw `admin` perm which is only
  // true for base-role admins (not TL or other custom roles).
  const showRuleBook = canSee("admin");
  const firstAllowed = [
    showCampaigns && "campaigns",
    showAgents && "agents",
    showUsers && "users",
    showShifts && "shifts",
    showExclusions && "exclusions",
    showEmailCampaigns && "email_campaigns",
    showProductivity && "productivity",
    showReports && "reports",
    showShiftSchedules && "shift_schedules",
  ].filter(Boolean)[0];
  const defaultTab = firstAllowed || "campaigns";
  // Deep-link support: /admin?tab=shift_schedules (used by the pending
  // shift-extension banner). Controlled tabs so a banner click while
  // already on /admin also switches.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || defaultTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  const loadAgents = async () => {
    const a = await api.get("/agents");
    setAgents(a.data);
  };
  const loadRoles = async () => {
    try {
      const r = await api.get("/roles");
      setAdminRoles(r.data);
    } catch (e) { /* ignore — UsersTab also loads */ }
  };
  const loadCampaignCount = async () => {
    const c = await api.get("/campaigns");
    setCampaignsCount(c.data.length);
  };
  useEffect(() => { loadAgents(); loadCampaignCount(); loadRoles(); }, []);

  const createAgent = async (payload) => {
    try {
      await api.post("/agents", { name: payload.name, email: payload.email || "", role: payload.role || "" });
      await loadAgents(); await refreshOptions();
      toast.success("Agent created");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updateAgent = async (id, payload) => {
    try {
      await api.put(`/agents/${id}`, { name: payload.name, email: payload.email || "", role: payload.role || "" });
      await loadAgents(); await refreshOptions();
      toast.success("Agent updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteAgent = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    try {
      await api.delete(`/agents/${id}`);
      await loadAgents(); await refreshOptions();
      toast.success("Agent deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Settings"
        title="Admin Panel"
        description="Manage campaigns (with targeting parameters), agents, and team members."
        testid="admin-header"
      />
      <div className="p-8 max-w-6xl">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "campaigns") loadCampaignCount(); refreshOptions(); }}>
          {/* Tab bar wraps to multiple lines instead of overflowing off-
              screen — admin panel keeps growing (14+ tabs) so a fixed
              inline row would clip the rightmost items on smaller
              viewports. `flex-wrap` + `h-auto` lets the row grow
              vertically; `justify-start` keeps ordering left-to-right.
              Extra vertical gap between wrapped rows for legibility. */}
          <TabsList className="rounded-sm flex flex-wrap h-auto justify-start gap-y-1 p-1">
            {showCampaigns && <TabsTrigger value="campaigns" data-testid="admin-tab-campaigns" className="rounded-sm">Campaigns ({campaignsCount})</TabsTrigger>}
            {showAgents && <TabsTrigger value="agents" data-testid="admin-tab-agents" className="rounded-sm">Agents ({agents.length})</TabsTrigger>}
            {showUsers && <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-sm">Users &amp; Permissions</TabsTrigger>}
            {showShifts && <TabsTrigger value="shifts" data-testid="admin-tab-shifts" className="rounded-sm">Shifts</TabsTrigger>}
            {showExclusions && <TabsTrigger value="exclusions" data-testid="admin-tab-exclusions" className="rounded-sm">Exclusion Lists</TabsTrigger>}
            {showEmailCampaigns && <TabsTrigger value="email_campaigns" data-testid="admin-tab-email-campaigns" className="rounded-sm">Email Campaigns</TabsTrigger>}
            {showProductivity && <TabsTrigger value="productivity" data-testid="admin-tab-productivity" className="rounded-sm">Productivity</TabsTrigger>}
            {showReports && <TabsTrigger value="reports" data-testid="admin-tab-reports" className="rounded-sm">Reports &amp; Analytics</TabsTrigger>}
            {showTeamMeetings && <TabsTrigger value="team_meetings" data-testid="admin-tab-team-meetings" className="rounded-sm">Team Meetings</TabsTrigger>}
            {showShiftSchedules && <TabsTrigger value="shift_schedules" data-testid="admin-tab-shift-schedules" className="rounded-sm">Shift Schedules</TabsTrigger>}
            {showSystemHealth && <TabsTrigger value="system_health" data-testid="admin-tab-system-health" className="rounded-sm">System Health</TabsTrigger>}
            {showSupport && <TabsTrigger value="support" data-testid="admin-tab-support" className="rounded-sm">Support</TabsTrigger>}
            {showSbtSite && <TabsTrigger value="sbt_site" data-testid="admin-tab-sbt-site" className="rounded-sm">SBT Site</TabsTrigger>}
            {showSbtSite && <TabsTrigger value="growth_engine" data-testid="admin-tab-growth-engine" className="rounded-sm">AI Growth Engine</TabsTrigger>}
            {showRuleBook && <TabsTrigger value="rule_book" data-testid="admin-tab-rule-book" className="rounded-sm">Rule Book</TabsTrigger>}
          </TabsList>
          {showCampaigns && (
            <TabsContent value="campaigns" className="mt-6">
              <CampaignsTab options={options} />
            </TabsContent>
          )}
          {showAgents && (
            <TabsContent value="agents" className="mt-6">
              <AgentsTab
                agents={agents}
                roles={adminRoles}
                onCreate={createAgent}
                onUpdate={updateAgent}
                onDelete={deleteAgent}
                onRefresh={loadAgents}
                onRolesRefresh={loadRoles}
              />
            </TabsContent>
          )}
          {showUsers && (
            <TabsContent value="users" className="mt-6">
              <UsersTab currentUser={user} onRolesChanged={loadRoles} onEditPermissions={setPermsRole} />
            </TabsContent>
          )}
          {showShifts && (
            <TabsContent value="shifts" className="mt-6">
              <ShiftsTab />
            </TabsContent>
          )}
          {showExclusions && (
            <TabsContent value="exclusions" className="mt-6">
              <AdminExclusionLists />
            </TabsContent>
          )}
          {showEmailCampaigns && (
            <TabsContent value="email_campaigns" className="mt-6">
              <AdminEmailCampaigns />
            </TabsContent>
          )}
          {showProductivity && (
            <TabsContent value="productivity" className="mt-6">
              <AdminProductivity readOnly={!canEditProductivity} />
            </TabsContent>
          )}
          {showReports && (
            <TabsContent value="reports" className="mt-6">
              <AdminReports />
            </TabsContent>
          )}
          {showTeamMeetings && (
            <TabsContent value="team_meetings" className="mt-6">
              <TeamMeetings />
            </TabsContent>
          )}
          {showShiftSchedules && (
            <TabsContent value="shift_schedules" className="mt-6">
              <AdminShiftSchedules />
            </TabsContent>
          )}
          {showSystemHealth && (
            <TabsContent value="system_health" className="mt-6">
              <AdminSystemHealth />
            </TabsContent>
          )}
          {showSupport && (
            <TabsContent value="support" className="mt-6">
              <AdminSupport />
            </TabsContent>
          )}
          {showSbtSite && (
            <TabsContent value="sbt_site" className="mt-6">
              <AdminSbtSite />
            </TabsContent>
          )}
          {showSbtSite && (
            <TabsContent value="growth_engine" className="mt-6">
              <AdminGrowthEngine />
            </TabsContent>
          )}
          {showRuleBook && (
            <TabsContent value="rule_book" className="mt-6">
              <AdminRuleBook />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <RolePermissionsDialog
        role={permsRole}
        open={!!permsRole}
        onClose={() => setPermsRole(null)}
        onSaved={loadRoles}
      />
    </div>
  );
}
